import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { AlertNotification, Button, LabeledIconInput, Row, Col } from "@commonsku/styles";

import { ClientCreditMemo } from "../../types";
import { getCurrencySymbol } from "../../utils";

import useDraft from "./useDraft";
import FormPopup from "../FormPopup";
import ExemptTaxSelect from "../helpers/ExemptTaxSelect";
import AsyncClientSelect from "../project/AsyncClientSelect";
import useCanadianTax from "../../hooks/useCanadianTax";

const CLIENT_CREDIT_MEMO_POPUP_WIDTH = "640px";
const CLIENT_CREDIT_MEMO_POPUP_HEIGHT = "324px";
const CREDIT_MEMO_NOTE_PLACEHOLDER_TEXT = "Add a note (optional)";

export default function ClientCreditMemoPopup({
  creditMemo,
  onClose,
  isDisabled = false,
}: {
  creditMemo: ClientCreditMemo;
  onClose: (saved: boolean) => void;
  isDisabled?: boolean;
}) {
  const { draft, isNew, isValid, isEditable, updater, save } = useDraft(creditMemo);
  const disabled = isDisabled || !isEditable;
  const { isCanadaQBO } = useCanadianTax();
  const [saved, setSaved] = useState(false);

  if ("CLIENT" !== draft.type) {
    throw new Error("Invalid credit memo");
  }

  const handleError = useCallback(
    (error: unknown) => toast.error("Unable to create credit memo"),
    [],
  );

  const handleSave = useCallback(
    async () => {
      try {
        await save();
        setSaved(true);
      } catch (e) {
        toast.error(
          isNew ?
            "Unable to create credit memo" :
            "Unable to save credit memo"
        );
      }
    },
    [isNew, save]
  );

  const handleClose = useCallback(
    () => onClose(saved),
    [onClose, saved]
  );

  const title = isNew
    ? "Create Credit Memo"
    : `Credit Memo ${creditMemo.number}`;

  if (saved) {
    return (
      <FormPopup
        isNew={isNew}
        isEditable={false}
        isValid={isValid}
        onClose={handleClose}
        title={title}
        width={CLIENT_CREDIT_MEMO_POPUP_WIDTH}
        height={CLIENT_CREDIT_MEMO_POPUP_HEIGHT}
        customActions={<>
          <Button as="a" secondary href="/finance/credit-memos">
            View Credit Memos
          </Button>
          <Button onClick={handleClose}>
            Close
          </Button>
        </>}
      >
        <AlertNotification alertType="success">
          {isNew ? 'Credit memo created' : 'Credit memo updated'}
        </AlertNotification>
        <span>You can find your credit memos in the <b>Credit Memos</b> tab of Invoicing</span>
      </FormPopup>
    );
  }

  return (
    <FormPopup
      title={title}
      onClose={handleClose}
      onSave={handleSave}
      isNew={isNew}
      isValid={isValid}
      isEditable={!disabled}
      width={CLIENT_CREDIT_MEMO_POPUP_WIDTH}
      height={CLIENT_CREDIT_MEMO_POPUP_HEIGHT}
    >
      <Row>
        <Col>
          <textarea
            style={{ minHeight: "4.5rem", margin: 0 }}
            value={draft.notes}
            onChange={(e) => updater.setNotes(e.target.value)}
            onBlur={(e) => updater.setNotes(draft.notes.trim())}
            placeholder={CREDIT_MEMO_NOTE_PLACEHOLDER_TEXT}
            disabled={disabled}
          />
        </Col>
      </Row>
      <Row style={{ gap: "1rem" }}>
        <Col>
          <AsyncClientSelect
            label="Client"
            labelStyle={{ lineHeight: "24px" }}
            name="client_id"
            value={draft.client.id}
            onChange={(client) => updater.setClient(client)}
            isDisabled={disabled}
          />
        </Col>
        {isCanadaQBO && (
          <Col>
            <ExemptTaxSelect
              value={draft.tax_id}
              label={"Tax Code"}
              labelStyle={{ lineHeight: "24px" }}
              onChange={(tax_id) => updater.setTaxId(tax_id)}
              isDisabled={disabled}
            />
          </Col>
        )}
        <Col>
          <LabeledIconInput
            label="Total amount to refund"
            labelOnTop
            Icon={<span>{getCurrencySymbol("en-US", draft.currency)}</span>}
            value={draft.total}
            onChange={(e) => updater.setTotal(e.target.value)}
            onBlur={(e) => updater.setTotal(Number.isNaN(Number(draft.total)) ? '0.00' : Number(draft.total).toFixed(2))}
            onFocus={(e) => e.target.select()}
          />
        </Col>
      </Row>
    </FormPopup>
  );
}
